import { readFileSync, writeFileSync, existsSync } from 'fs';
import { join } from 'path';
import * as babel from '@babel/core';
import _traverse from '@babel/traverse';
import _generate from '@babel/generator';
import * as t from '@babel/types';

// Handle CommonJS/ESM compatibility
const traverse = (_traverse as any).default || _traverse;
const generate = (_generate as any).default || _generate;

export interface NextConfigUpdate {
  experimental?: Record<string, any>;
  headers?: () => Promise<any[]>;
  rewrites?: () => Promise<any>;
  transpilePackages?: string[];
}

export class NextConfigUpdater {
  private configPath: string = '';
  private originalContent: string | null = null;

  constructor(projectRoot: string) {
    // Try to find next.config.js, .mjs, or .ts
    const possiblePaths = [
      'next.config.js',
      'next.config.mjs',
      'next.config.ts',
    ];

    for (const path of possiblePaths) {
      const fullPath = join(projectRoot, path);
      if (existsSync(fullPath)) {
        this.configPath = fullPath;
        break;
      }
    }

    if (!this.configPath) {
      throw new Error('No next.config file found');
    }
  }

  async update(updates: NextConfigUpdate): Promise<void> {
    // Read the current config
    this.originalContent = readFileSync(this.configPath, 'utf-8');
    
    // For TypeScript configs, we'll need to handle differently
    if (this.configPath.endsWith('.ts')) {
      await this.updateTypeScriptConfig(updates);
    } else {
      await this.updateJavaScriptConfig(updates);
    }
  }

  rollback(): void {
    if (this.originalContent) {
      writeFileSync(this.configPath, this.originalContent);
    }
  }

  private async updateJavaScriptConfig(updates: NextConfigUpdate): Promise<void> {
    const ast = babel.parseSync(this.originalContent!, {
      filename: this.configPath,
      presets: [],
      plugins: [],
    });

    if (!ast) {
      throw new Error('Failed to parse next.config.js');
    }

    let configObject: t.ObjectExpression | null = null;
    let isModuleExports = false;

    // Find the config object
    traverse(ast, {
      // Handle module.exports = { ... }
      AssignmentExpression(path: any) {
        if (
          t.isMemberExpression(path.node.left) &&
          t.isIdentifier(path.node.left.object, { name: 'module' }) &&
          t.isIdentifier(path.node.left.property, { name: 'exports' }) &&
          t.isObjectExpression(path.node.right)
        ) {
          configObject = path.node.right;
          isModuleExports = true;
        }
      },
      // Handle export default { ... }
      ExportDefaultDeclaration(path: any) {
        if (t.isObjectExpression(path.node.declaration)) {
          configObject = path.node.declaration;
        }
      },
    });

    if (!configObject) {
      throw new Error('Could not find Next.js config object');
    }

    // Apply updates to the config object
    this.applyUpdatesToObject(configObject, updates);

    // Generate the updated code
    const { code } = generate(ast, {
      retainLines: true,
      retainFunctionParens: true,
    });

    writeFileSync(this.configPath, code);
  }

  private async updateTypeScriptConfig(updates: NextConfigUpdate): Promise<void> {
    // For TypeScript configs, we'll use a simpler approach
    // This is a basic implementation - could be enhanced with proper TS AST parsing
    let content = this.originalContent!;

    // Add experimental features if needed
    if (updates.experimental) {
      const experimentalStr = JSON.stringify(updates.experimental, null, 2)
        .split('\n')
        .map((line, i) => i === 0 ? line : '    ' + line)
        .join('\n');

      if (content.includes('experimental:')) {
        // Merge with existing experimental config
        console.warn('Existing experimental config found. Manual merge may be required.');
      } else {
        // Add experimental config
        content = content.replace(
          /const nextConfig = {/,
          `const nextConfig = {\n  experimental: ${experimentalStr},`
        );
      }
    }

    // Add transpilePackages if needed
    if (updates.transpilePackages) {
      const packagesStr = JSON.stringify(updates.transpilePackages);
      if (!content.includes('transpilePackages:')) {
        content = content.replace(
          /const nextConfig = {/,
          `const nextConfig = {\n  transpilePackages: ${packagesStr},`
        );
      }
    }

    writeFileSync(this.configPath, content);
  }

  private applyUpdatesToObject(obj: t.ObjectExpression, updates: NextConfigUpdate): void {
    // Add experimental config
    if (updates.experimental) {
      this.addOrUpdateProperty(obj, 'experimental', 
        t.objectExpression(
          Object.entries(updates.experimental).map(([key, value]) =>
            t.objectProperty(
              t.identifier(key),
              t.booleanLiteral(value as boolean)
            )
          )
        )
      );
    }

    // Add transpilePackages
    if (updates.transpilePackages) {
      this.addOrUpdateProperty(obj, 'transpilePackages',
        t.arrayExpression(
          updates.transpilePackages.map(pkg => t.stringLiteral(pkg))
        )
      );
    }
  }

  private addOrUpdateProperty(obj: t.ObjectExpression, key: string, value: t.Expression): void {
    const existingProp = obj.properties.find(
      prop => t.isObjectProperty(prop) && t.isIdentifier(prop.key, { name: key })
    );

    if (existingProp && t.isObjectProperty(existingProp)) {
      existingProp.value = value;
    } else {
      obj.properties.push(t.objectProperty(t.identifier(key), value));
    }
  }
}